/**
 * AdminShell — professional light-themed admin layout with grouped left
 * sidebar navigation, top bar, and content area. Wraps the existing admin
 * tab content so we get a modern shell without rewriting the panels.
 *
 * Usage:
 *   <AdminShell
 *     activeTab={activeTab}
 *     onTabChange={setActiveTab}
 *     onLogout={handleLogout}
 *     onToolClick={{ betaTesters, icDisclaimers, medCardDisclaimers,
 *                    tutorials, emailActivity, resetDisclaimer }}
 *   >
 *     {tab content children}
 *   </AdminShell>
 */
import React, { useState } from 'react';
import {
  LayoutDashboard, Building2, Users, FileText, BookOpen, Shield, Mail,
  BarChart3, MessageSquare, Server, HardHat, MessagesSquare, Star, Gift,
  Sparkles, Megaphone, ChevronDown, ChevronRight, Menu, X, LogOut,
  Bell, Settings, TestTube, ClipboardList, FileWarning, DollarSign, Video, Activity, ShieldCheck
} from 'lucide-react';

const NAV_GROUPS = [
  {
    label: 'Overview',
    items: [
      { id: 'dashboard', label: 'Dashboard', Icon: LayoutDashboard },
      { id: 'analytics', label: 'Analytics',  Icon: BarChart3 },
      { id: 'app-health', label: 'App Health', Icon: Activity },
      { id: 'welcome-video', label: 'Welcome Video', Icon: Video },
    ],
  },
  {
    label: 'Practices & Users',
    items: [
      { id: 'practices',      label: 'Practices',         Icon: Building2 },
      { id: 'beta-program',   label: 'Beta Program',      Icon: TestTube },
      { id: 'offices-plans',  label: 'Offices & Plans',   Icon: Building2 },
      { id: 'enterprise-inquiries', label: 'Enterprise Inquiries', Icon: ClipboardList },
      { id: 'smile-links',    label: 'SmileDesign Links', Icon: Sparkles },
      { id: 'practice-info',  label: 'Practice Info',     Icon: ClipboardList },
      { id: 'users',          label: 'Users',             Icon: Users },
      { id: 'registrations',  label: 'New Registrations', Icon: HardHat },
    ],
  },
  {
    label: 'Content Library',
    items: [
      { id: 'procedures',      label: 'Procedures',      Icon: FileText },
      { id: 'testimonials',    label: 'Testimonials',    Icon: Star },
      { id: 'leadmagnet',      label: 'Lead Magnet',     Icon: Gift },
      { id: 'ecard-requests',  label: 'Patient Education Studio',  Icon: Mail },
      { id: 'announcements',   label: 'Announcements',   Icon: Megaphone },
    ],
  },
  {
    label: 'Staff Marketplace',
    items: [
      { id: 'dentalstaff',  label: 'DentalStaff',  Icon: MessagesSquare },
      { id: 'reinterview',  label: 'Re-interview', Icon: MessageSquare },
    ],
  },
  {
    label: 'Revenue',
    items: [
      { id: 'billing',  label: 'Billing & Plans',  Icon: DollarSign },
    ],
  },
  {
    label: 'System',
    items: [
      { id: 'support',        label: 'Support',         Icon: MessageSquare },
      { id: 'compliance',     label: 'Compliance',      Icon: Shield },
      { id: 'baa',            label: 'BAA Agreements',  Icon: ShieldCheck },
      { id: 'sms-diagnostics',label: 'SMS Diagnostics', Icon: MessagesSquare },
      { id: 'system',         label: 'System',          Icon: Server },
    ],
  },
];

const TOOL_ITEMS = [
  { key: 'betaTesters',        label: 'Beta Testers',       Icon: TestTube },
  { key: 'icDisclaimers',      label: 'IC Disclaimers',     Icon: FileWarning },
  { key: 'medCardDisclaimers', label: 'Med Card Disclaimers', Icon: FileWarning },
  { key: 'tutorials',          label: 'Tutorials',          Icon: BookOpen },
  { key: 'emailActivity',      label: 'Email Activity',     Icon: Bell },
  { key: 'resetDisclaimer',    label: 'Reset Disclaimer',   Icon: Shield },
];

export default function AdminShell({ activeTab, onTabChange, onLogout, onToolClick = {}, children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false); // mobile
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [toolsOpen, setToolsOpen] = useState(false);

  const toggleGroup = (label) => {
    setCollapsedGroups(prev => ({ ...prev, [label]: !prev[label] }));
  };

  const activeGroup = NAV_GROUPS.find(g => g.items.some(i => i.id === activeTab));
  const activeItem = activeGroup?.items.find(i => i.id === activeTab);

  const sidebarContent = (
    <>
      {/* Sidebar header */}
      <div className="px-5 py-5 border-b border-slate-200 flex items-center gap-3">
        <img src="/practcom-logo.png" alt="PractCom" className="h-9 w-auto rounded-md" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
        <div>
          <div className="text-sm font-bold text-slate-900">PractCom</div>
          <div className="text-xs text-slate-500">Admin Console</div>
        </div>
      </div>

      {/* Nav groups */}
      <nav className="flex-1 overflow-y-auto py-3">
        {NAV_GROUPS.map(group => {
          const collapsed = collapsedGroups[group.label];
          return (
            <div key={group.label} className="mb-1">
              <button
                onClick={() => toggleGroup(group.label)}
                className="w-full flex items-center justify-between px-5 py-2 text-xs font-semibold uppercase tracking-wider text-slate-400 hover:text-slate-600"
              >
                <span>{group.label}</span>
                {collapsed
                  ? <ChevronRight className="h-3.5 w-3.5" />
                  : <ChevronDown className="h-3.5 w-3.5" />}
              </button>
              {!collapsed && (
                <div className="space-y-0.5 px-3">
                  {group.items.map(item => {
                    const Icon = item.Icon;
                    const active = activeTab === item.id;
                    return (
                      <button
                        key={item.id}
                        onClick={() => { onTabChange(item.id); setSidebarOpen(false); }}
                        data-testid={`admin-nav-${item.id}`}
                        className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors
                          ${active
                            ? 'bg-blue-50 text-blue-700 border-l-4 border-blue-600 pl-2'
                            : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
                      >
                        <Icon className={`h-4 w-4 shrink-0 ${active ? 'text-blue-600' : 'text-slate-400'}`} />
                        <span className="truncate">{item.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {/* Tools & Modals */}
        <div className="mt-4 border-t border-slate-100 pt-3">
          <button
            onClick={() => setToolsOpen(o => !o)}
            className="w-full flex items-center justify-between px-5 py-2 text-xs font-semibold uppercase tracking-wider text-slate-400 hover:text-slate-600"
            data-testid="admin-tools-toggle"
          >
            <span className="flex items-center gap-2"><Settings className="h-3.5 w-3.5" /> Tools</span>
            {toolsOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          </button>
          {toolsOpen && (
            <div className="space-y-0.5 px-3">
              {TOOL_ITEMS.map(tool => {
                const Icon = tool.Icon;
                const handler = onToolClick[tool.key];
                if (!handler) return null;
                return (
                  <button
                    key={tool.key}
                    onClick={handler}
                    data-testid={`admin-tool-${tool.key}`}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  >
                    <Icon className="h-4 w-4 text-slate-400" />
                    <span className="truncate">{tool.label}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </nav>

      {/* Footer / logout */}
      <div className="px-3 py-3 border-t border-slate-200">
        <button
          onClick={onLogout}
          data-testid="admin-sidebar-logout"
          className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-rose-600 hover:bg-rose-50"
        >
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-72 bg-white border-r border-slate-200 flex flex-col
          transition-transform duration-200 lg:translate-x-0 lg:static lg:shrink-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}
      >
        {sidebarContent}
      </aside>

      {/* Main area */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Top bar */}
        <header className="sticky top-0 z-20 bg-white border-b border-slate-200 h-16 flex items-center px-4 lg:px-6 gap-3">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden p-2 -ml-2 rounded-md hover:bg-slate-100"
            aria-label="Open sidebar"
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="min-w-0 flex-1">
            <div className="text-xs text-slate-500">{activeGroup?.label || 'Admin'}</div>
            <div className="text-lg font-semibold text-slate-900 truncate">
              {activeItem?.label || 'Admin Console'}
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-2 text-xs text-slate-500">
            <span className="inline-flex h-2 w-2 rounded-full bg-emerald-500" />
            System healthy
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 p-4 lg:p-8 max-w-full">
          {children}
        </main>
      </div>
    </div>
  );
}
